import { useState, useEffect } from 'react';
import { createRoute, redirect } from '@tanstack/react-router';
import { useAuth0 } from '@auth0/auth0-react';
import { z } from 'zod';
import { MoveDown } from 'lucide-react';
import { Route as rootRoute } from '../__root';
import { Layout } from '@/client/layouts/layout';
import { Input } from '@/client/components/ui/input';
import { Button } from '@/client/components/ui/button';
import { Label } from '@/client/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/client/components/ui/dialog';
import { Spinner } from '@/client/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/client/components/ui/table';
import { callCrudApi } from '@/client/utils/crud';
import { useClientsStore } from '@/client/store/clientsStore';
import type { SwapUserClientPayload } from '@shared/types/crud';
import type { UserMetadata } from '@shared/types';

const USER_METADATA_KEY = 'http://invintus_user_metadata';

// Validation schema for swap form
const swapSchema = z.object({
  userID: z.string().min(1, 'User ID is required').regex(/^\d+$/, 'User ID must be a number'),
  clientID: z.string().length(10, 'Client ID invalid').regex(/^\d+$/, 'Client ID must be a number'),
});

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: 'utilities/swap-user-client',
  beforeLoad: async ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/' });
    }
  },
  component: SwapUserClientComponent,
});

function SwapUserClientComponent() {
  const {
    getAccessTokenSilently,
    loginWithRedirect,
    user,
    logout
  } = useAuth0();

  const [userID, setUserID] = useState('');
  const [clientID, setClientID] = useState('');
  const [initialUserID, setInitialUserID] = useState('');
  const [initialClientID, setInitialClientID] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [clientFilter, setClientFilter] = useState('');

  const { clients, fetchClients, loading: clientsLoading } = useClientsStore();

  // Pre-populate from user metadata
  useEffect(() => {
    if (user) {
      const metadata = user[USER_METADATA_KEY] as UserMetadata | undefined;
      if (metadata) {
        if (metadata.loginID !== undefined) {
          const loginIDStr = String(metadata.loginID);
          setUserID(loginIDStr);
          setInitialUserID(loginIDStr);
        }
        if (metadata.clientID !== undefined) {
          const clientIDStr = String(metadata.clientID);
          setClientID(clientIDStr);
          setInitialClientID(clientIDStr);
        }
      }
    }
  }, [user]);

  // Fetch clients on mount
  useEffect(() => {
    const loadClients = async () => {
      const token = await getAccessTokenSilently();
      await fetchClients(token, logout);
    };
    if (clients.length === 0) {
      loadClients();
    }
  }, [getAccessTokenSilently, fetchClients, clients.length]);

  // Filter active clients, apply search filter, and sort alphabetically
  const activeClients = clients
    .filter((client) => client.active === 1)
    .filter((client) =>
      clientFilter === '' ||
      client.name.toLowerCase().includes(clientFilter.toLowerCase())
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  const hasChanges = userID !== initialUserID || clientID !== initialClientID;
  const canSubmit = !loading && userID && clientID && hasChanges;

  const initialClientName = clients.find((c) => c.clientID === initialClientID)?.name;
  const newClientName = clients.find((c) => c.clientID === clientID)?.name;

  const handleClientSelect = (selectedClientID: string) => {
    setClientID(selectedClientID);
    setDialogOpen(false);
    setClientFilter('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    const validation = swapSchema.safeParse({ userID, clientID });
    if (!validation.success) {
      setError(validation.error.issues[0].message);
      return;
    }

    setLoading(true);

    try {
      const accessToken = await getAccessTokenSilently();
      const payload: SwapUserClientPayload = { userID, clientID };
      const response = await callCrudApi('/Users/swapUserAcc', payload, accessToken);

      if (response.success) {
        setSuccess(true);
        // Silent re-auth to refresh user metadata from Auth0 after delay
        setTimeout(() => {
          loginWithRedirect({
            authorizationParams: { prompt: 'none' },
            appState: { returnTo: window.location.pathname },
          });
        }, 4000);
      } else {
        setError('Operation failed. Please check the IDs and try again.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div>
        <section>
          <h3 className="text-2xl font-semibold mb-4">Swap User Client</h3>
          <p className="text-muted-foreground mb-6">
            This utility allows you to swap a user's client association.
          </p>

          <form onSubmit={handleSubmit} className="max-w-md space-y-4">
            <div className="space-y-2">
              <Label htmlFor="userID">User ID</Label>
              <Input
                id="userID"
                type="text"
                value={userID}
                onChange={(e) => setUserID(e.target.value)}
                placeholder="Enter user ID"
                required
                disabled={loading}
                className="font-mono"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="clientID">Client ID</Label>
              <div className="flex gap-2">
                <Input
                  id="clientID"
                  type="text"
                  value={clientID}
                  onChange={(e) => setClientID(e.target.value)}
                  placeholder="Enter client ID"
                  required
                  disabled={loading}
                  className="font-mono"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(true)}
                  disabled={loading}
                >
                  Browse
                </Button>
              </div>
            </div>

            {error && (
              <div className="p-3 text-sm text-red-500 bg-red-50 dark:bg-red-950 rounded-md">
                {error}
              </div>
            )}

            <Button type="submit" disabled={!canSubmit}>
              {loading ? 'Processing...' : 'Swap Client'}
            </Button>
          </form>
        </section>
      </div>

      {/* Success Dialog - prevents interaction until page refreshes */}
      <Dialog open={success}>
        <DialogContent
          className="max-w-sm"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          hideCloseButton
        >
          <DialogTitle className="sr-only">Success</DialogTitle>
          <div className="flex flex-col items-center justify-center py-6 gap-4">
            <Spinner className="size-8" />
            <p className="text-lg font-medium text-center">
              <span>Success!</span>
            </p>

            {initialClientName && newClientName && (
              <div className="flex flex-col items-center gap-1 text-base bg-muted rounded-md p-3">
                <span className="text-muted-foreground">{initialClientName}</span>
                <MoveDown className="size-5" />
                <span className="font-bold">{newClientName}</span>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Client Selection Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => {
        setDialogOpen(open);
        if (!open) setClientFilter('');
      }}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col top-[10%] translate-y-0">
          <DialogHeader>
            <DialogTitle>Select Client</DialogTitle>
            <DialogDescription>
              Choose an active client from the list below.
            </DialogDescription>
          </DialogHeader>
          <Input
            type="text"
            placeholder="Filter by client name..."
            value={clientFilter}
            onChange={(e) => setClientFilter(e.target.value)}
            className="mb-2"
          />
          <div className="overflow-auto flex-1">
            {clientsLoading ? (
              <div className="py-8 text-center text-muted-foreground">
                Loading clients...
              </div>
            ) : activeClients.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground">
                No active clients found.
              </div>
            ) : (
              <Table className="table-fixed">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-32">Client ID</TableHead>
                    <TableHead>Name</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activeClients.map((client) => (
                    <TableRow
                      key={client.clientID}
                      className="cursor-pointer hover:bg-muted"
                      onClick={() => handleClientSelect(client.clientID)}
                    >
                      <TableCell className="font-mono">{client.clientID}</TableCell>
                      <TableCell>{client.name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
